import type { ReactNode } from 'react';
import styles from './Icon.module.css';

/**
 * **`Icon`**（`docs/design-system.md` §3.4 / §5.1）。
 *
 * ## 型で守っていること
 *
 * - **サイズは 16 / 20 / 24 の 3 段だけ**（**24 の約数か倍数**。中間の値は
 *   エッジが滲む）。**任意の数値を受け取らない**
 * - **色の prop を持たない**（`currentColor` を継ぐ。§3.4:
 *   **アイコンに独自の色を割り当てない**）
 * - **`label` を渡さないと `aria-hidden` になる**（§3.4 の読み上げ規約）。
 *   **アイコンだけで意味を伝えないため、ラベルがあるのが既定ではない** —
 *   **ラベルを添えた文脈で使うのが既定**で、単独で使うときだけ `label` を渡す
 *
 * ## SVG の中身をどう渡すか
 *
 * `icons.ts` の `loadIcon` は `node:fs` を使うので**ブラウザでは動かない**。
 * **呼び出し側がビルド時に読み込んだ文字列を渡す**（Vite の `?raw` など）。
 * **この部品はファイルを読まない** — 読むと**ブラウザ用のバンドルに
 * `node:fs` が混入する**。
 */
export interface IconProps {
  /** 加工済みの SVG の中身（`<svg>` の内側）。**外枠は付けない** */
  readonly svg: string;
  readonly size?: 16 | 20 | 24;
  /**
   * **単独で使うときだけ渡す**（§3.4）。
   * 渡すと `role="img"` + `aria-label`、渡さないと `aria-hidden="true"`。
   * **アイコンだけで意味を伝えない**のが原則なので、**既定は装飾**である。
   */
  readonly label?: string;
}

export function Icon({ svg, size = 20, label }: IconProps): ReactNode {
  const sizeClass = size === 16 ? styles['s16'] : size === 24 ? styles['s24'] : styles['s20'];
  return (
    <svg
      className={[styles['icon'], sizeClass].join(' ')}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...(label === undefined
        ? { 'aria-hidden': true as const }
        : { role: 'img' as const, 'aria-label': label })}
      /**
       * **加工済みの文字列だけを入れる。**
       * 原本は `fill="#1A1A1C"` が焼き込まれており、
       * **そのまま入れるとダークテーマで見えなくなる**（§3.4 加工 1）。
       * 加工は `icons.ts` が行い、`icons.test.ts` が全件で確かめている。
       */
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
